"use client"

import type React from "react"
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react"
import { type User, onAuthStateChanged, signOut } from "firebase/auth"
import { auth } from "../lib/firebase"
import { useEmailVerification } from "../hooks/useEmailVerification"
import AuthModal from "@/components/AuthModal"

type AuthModalContext = "normal" | "purchase"

interface AuthContextType {
  user: User | null
  loading: boolean
  isEmailVerified: boolean
  isAuthModalOpen: boolean
  authModalContext: AuthModalContext
  openAuthModal: (context?: AuthModalContext) => void
  closeAuthModal: () => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isEmailVerified: false,
  isAuthModalOpen: false,
  authModalContext: "normal",
  openAuthModal: () => {},
  closeAuthModal: () => {},
  logout: async () => {},
})

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [authModalContext, setAuthModalContext] = useState<AuthModalContext>("normal")

  const isEmailVerified = useEmailVerification(user)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser)
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const openAuthModal = useCallback((context: AuthModalContext = "normal") => {
    setAuthModalContext(context)
    setIsAuthModalOpen(true)
  }, [])

  const closeAuthModal = useCallback(() => setIsAuthModalOpen(false), [])

  const logout = useCallback(async () => {
    try {
      await signOut(auth)
    } catch (error) {
      console.error("Logout error:", error)
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      isEmailVerified,
      isAuthModalOpen,
      authModalContext,
      openAuthModal,
      closeAuthModal,
      logout,
    }),
    [user, loading, isEmailVerified, isAuthModalOpen, authModalContext, openAuthModal, closeAuthModal, logout],
  )

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} context={authModalContext} />
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)

